/**
 * SRS Algorithm - FSRS spaced repetition + mastery logic
 *
 * Pure functions only. No Firestore or UI dependencies. Safe for unit testing.
 * FSRS reference: https://github.com/open-spaced-repetition/fsrs4anki
 */

// Mastery level thresholds
export const MASTERY_LEVELS = {
  BEGINNER: { min: 0, max: 30, name: 'Beginner' },
  INTERMEDIATE: { min: 31, max: 60, name: 'Intermediate' },
  ADVANCED: { min: 61, max: 90, name: 'Advanced' },
  MASTER: { min: 91, max: 100, name: 'Master' }
};

/** FSRS4Anki v6 default weights (global). */
export const DEFAULT_FSRS_WEIGHTS = [
  0.212, 1.2931, 2.3065, 8.2956, 6.4133, 0.8334, 3.0194, 0.001, 1.8722, 0.1666,
  0.796, 1.4835, 0.0614, 0.2629, 1.6483, 0.6014, 1.8729, 0.5425, 0.0912, 0.0658,
  0.1542
];

export const FSRS_REQUEST_RETENTION = 0.9;

export const SRS_MAX_INTERVAL_DAYS = 365;

/** Minimum accuracy (0–1) to count as a passing review. */
export const RATING_PASS_ACCURACY = 0.7;

/** Minimum accuracy (0–1) for a "good" recall without heavy struggle. */
export const RATING_GOOD_ACCURACY = 0.95;

/** Significant lines required for a single-slip pass (Hard) below RATING_PASS_ACCURACY. */
export const RATING_MIN_LINES_FOR_SLIP_PASS = 5;

/** Significant lines required for one-slip "good" when accuracy is 1 − 1/N. */
export const RATING_MIN_LINES_FOR_SLIP_GOOD = 3;

export const FSRS_RATINGS = {
  again: 1,
  hard: 2,
  good: 3,
  easy: 4
};

/** Card scheduling phase (Anki-style learning pipeline). */
export const CARD_PHASE = {
  NEW: 'new',
  LEARNING: 'learning',
  REVIEW: 'review',
  RELEARNING: 'relearning'
};

/**
 * Minute delays before the next learning/relearning step.
 * User must pass each step (good) before graduating to day intervals.
 */
export const LEARNING_STEPS_MINUTES = [10, 15];
export const RELEARNING_STEPS_MINUTES = [10, 1440];

/** First review interval (days) after completing all learning steps. */
export const GRADUATION_INTERVAL_DAYS = 1;

/** @typedef {'again' | 'hard' | 'good' | 'easy'} FsrsRatingName */

// Base mastery constants
export const BASE_MASTERY = {
  INITIAL: 20,
  CORRECT_INCREMENT: 10,
  DECAY_RATE: 0.1,
  MIN_MASTERY: 0,
  MAX_MASTERY: 100,
  FAILURE_PENALTY_FACTOR: -0.5
};

const FSRS_W = DEFAULT_FSRS_WEIGHTS;
const FSRS_DECAY = -FSRS_W[20];
const FSRS_FACTOR = 0.9 ** (1 / FSRS_DECAY) - 1;

/**
 * Normalize legacy boolean or numeric rating input.
 * @param {boolean | number | string | { rating?: string }} input
 * @returns {FsrsRatingName}
 */
export function normalizeRating(input) {
  if (input === true) return 'good';
  if (input === false) return 'again';
  if (typeof input === 'number') {
    const entry = Object.entries(FSRS_RATINGS).find(([, value]) => value === input);
    return /** @type {FsrsRatingName} */ (entry?.[0] || 'good');
  }
  if (input && typeof input === 'object' && input.rating) {
    return normalizeRating(input.rating);
  }
  if (typeof input === 'string' && FSRS_RATINGS[input] !== undefined) {
    return /** @type {FsrsRatingName} */ (input);
  }
  return 'good';
}

/**
 * Whether a rating counts as a successful recall for streaks / successfulReviews.
 * @param {FsrsRatingName} rating
 */
export function isSuccessfulRating(rating) {
  return FSRS_RATINGS[normalizeRating(rating)] >= FSRS_RATINGS.hard;
}

/**
 * Whether a solution line should contribute to accuracy (blank / comment-only lines are skipped).
 * @param {string} line
 */
export function isInsignificantLine(line) {
  const trimmed = String(line ?? '').trim();
  if (!trimmed) return true;
  if (/^(\/\/|#|--).*$/.test(trimmed)) return true;
  if (/^\/\*.*\*\/$/.test(trimmed)) return true;
  if (/^\*([^/]|\/*[^*/])*\*?\/?$/.test(trimmed)) return true;
  return false;
}

/**
 * @param {string} line
 * @returns {number}
 */
export function getLineWeight(line) {
  return isInsignificantLine(line) ? 0 : 1;
}

/**
 * Whether line accuracy counts as passing recall (Hard or better).
 * @param {{ accuracy: number, wrongLineCount?: number, totalLines?: number }} metrics
 */
export function isPassingRecallAccuracy({ accuracy, wrongLineCount = 0, totalLines = 1 }) {
  const safeAccuracy = Number.isFinite(accuracy) ? accuracy : 0;
  const lines = Math.max(1, totalLines || 1);
  const wrong = Math.max(0, wrongLineCount || 0);
  if (safeAccuracy >= RATING_PASS_ACCURACY) return true;
  return lines >= RATING_MIN_LINES_FOR_SLIP_PASS && wrong <= 1;
}

/**
 * Compare normalized user/solution lines and derive accuracy metrics.
 * Insignificant lines (blank, comment-only) are excluded from the denominator.
 * @param {string[]} userLines
 * @param {string[]} solutionLines
 * @param {(line: string) => string} [normalizeLine]
 */
export function computeLineAccuracy(userLines, solutionLines, normalizeLine = (line) => line) {
  let matchingLines = 0;
  let significantLines = 0;
  let weightedMatches = 0;
  let weightedTotal = 0;

  for (let i = 0; i < solutionLines.length; i += 1) {
    const solutionLine = normalizeLine(solutionLines[i] ?? '');
    const weight = getLineWeight(solutionLine);
    if (weight === 0) continue;

    significantLines += 1;
    weightedTotal += weight;
    const userLine = normalizeLine(userLines[i] ?? '');
    if (userLine === solutionLine) {
      matchingLines += 1;
      weightedMatches += weight;
    }
  }

  const totalLines = significantLines;
  const wrongLineCount = totalLines - matchingLines;
  const accuracy = weightedTotal > 0 ? weightedMatches / weightedTotal : 0;

  return {
    accuracy,
    wrongLineCount,
    matchingLines,
    totalLines,
    significantLines
  };
}

/**
 * Infer FSRS grade from line accuracy and first-try errors.
 * @param {{
 *   accuracy: number,
 *   initiallyWrongCount?: number,
 *   wrongLineCount?: number,
 *   totalLines?: number,
 *   completed?: boolean
 * }} params
 * @returns {FsrsRatingName}
 */
export function inferRating({
  accuracy,
  initiallyWrongCount = 0,
  wrongLineCount,
  totalLines = 1,
  completed = true
}) {
  const safeAccuracy = Number.isFinite(accuracy) ? accuracy : 0;
  const wrongFirstTry = Math.max(0, initiallyWrongCount || 0);
  const lines = Math.max(1, totalLines || 1);
  const wrongLines = Number.isFinite(wrongLineCount)
    ? Math.max(0, wrongLineCount)
    : Math.max(0, Math.round((1 - safeAccuracy) * lines));
  const errorRate = wrongFirstTry / lines;

  if (lines === 1) {
    if (!completed || safeAccuracy < 1) return 'again';
    return wrongFirstTry === 0 ? 'easy' : 'good';
  }

  if (!completed || !isPassingRecallAccuracy({
    accuracy: safeAccuracy,
    wrongLineCount: wrongLines,
    totalLines: lines
  })) {
    return 'again';
  }

  if (safeAccuracy === 1 && wrongFirstTry === 0) {
    return 'easy';
  }

  const oneSlipOnLongSnippet = lines >= RATING_MIN_LINES_FOR_SLIP_GOOD && wrongLines <= 1;
  if (safeAccuracy >= RATING_GOOD_ACCURACY && errorRate <= 1 / lines) {
    return 'good';
  }
  if (oneSlipOnLongSnippet && safeAccuracy >= 1 - 1 / lines && wrongFirstTry === 0) {
    return 'good';
  }

  return 'hard';
}

function constrainDifficulty(difficulty) {
  return Math.min(Math.max(+Number(difficulty).toFixed(2), 1), 10);
}

function meanReversion(init, current) {
  return FSRS_W[7] * init + (1 - FSRS_W[7]) * current;
}

function linearDamping(deltaD, oldD) {
  return deltaD * (10 - oldD) / 9;
}

/**
 * Default FSRS memory state for a brand-new card.
 */
export function createInitialFsrsState() {
  return {
    stability: initStability('good'),
    difficulty: initDifficulty('good')
  };
}

function initDifficulty(ratingName) {
  const rating = FSRS_RATINGS[ratingName];
  return +constrainDifficulty(
    FSRS_W[4] - Math.exp(FSRS_W[5] * (rating - 1)) + 1
  ).toFixed(2);
}

function initStability(ratingName) {
  const rating = FSRS_RATINGS[ratingName];
  return +Math.max(FSRS_W[rating - 1], 0.1).toFixed(2);
}

function nextDifficulty(d, ratingName) {
  const rating = FSRS_RATINGS[ratingName];
  const deltaD = -FSRS_W[6] * (rating - 3);
  const nextD = d + linearDamping(deltaD, d);
  return constrainDifficulty(meanReversion(initDifficulty('easy'), nextD));
}

export function forgettingCurve(elapsedDays, stability) {
  return (1 + FSRS_FACTOR * elapsedDays / stability) ** FSRS_DECAY;
}

function applyFuzz(ivl, seed = 0.5, previousInterval = 0) {
  if (ivl < 2.5) return ivl;
  const rounded = Math.round(ivl);
  let minIvl = Math.max(2, Math.round(rounded * 0.95 - 1));
  let maxIvl = Math.round(rounded * 1.05 + 1);
  if (previousInterval > 0 && rounded > previousInterval) {
    minIvl = Math.max(minIvl, previousInterval + 1);
  }
  const fuzzFactor = seed - Math.floor(seed);
  return Math.floor(fuzzFactor * (maxIvl - minIvl + 1) + minIvl);
}

function nextInterval(stability, seed = 0.5, previousInterval = 0) {
  const rawInterval = stability / FSRS_FACTOR * (
    FSRS_REQUEST_RETENTION ** (1 / FSRS_DECAY) - 1
  );
  const fuzzed = applyFuzz(rawInterval, seed, previousInterval);
  return Math.min(Math.max(Math.round(fuzzed), 1), SRS_MAX_INTERVAL_DAYS);
}

function nextRecallStability(d, s, r, ratingName) {
  const hardPenalty = ratingName === 'hard' ? FSRS_W[15] : 1;
  const easyBonus = ratingName === 'easy' ? FSRS_W[16] : 1;
  return +(s * (1 + Math.exp(FSRS_W[8]) *
    (11 - d) *
    s ** -FSRS_W[9] *
    (Math.exp((1 - r) * FSRS_W[10]) - 1) *
    hardPenalty *
    easyBonus)).toFixed(2);
}

function nextForgetStability(d, s, r) {
  const sMin = s / Math.exp(FSRS_W[17] * FSRS_W[18]);
  return +Math.min(
    FSRS_W[11] *
      d ** -FSRS_W[12] *
      ((s + 1) ** FSRS_W[13] - 1) *
      Math.exp((1 - r) * FSRS_W[14]),
    sMin
  ).toFixed(2);
}

function nextShortTermStability(s, ratingName) {
  const rating = FSRS_RATINGS[ratingName];
  let sinc = Math.exp(FSRS_W[17] * (rating - 3 + FSRS_W[18])) * s ** -FSRS_W[19];
  if (rating >= 3) {
    sinc = Math.max(sinc, 1);
  }
  return +(s * sinc).toFixed(2);
}

/** FSRS short-term memory update for learning/relearning button presses. */
function applyLearningMemoryUpdate(stability, difficulty, ratingName) {
  const rating = normalizeRating(ratingName);
  return {
    stability: nextShortTermStability(stability, rating),
    difficulty: nextDifficulty(difficulty, rating)
  };
}

function hashSeed(value) {
  const str = String(value ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return (Math.abs(hash) % 10000) / 10000;
}

/**
 * Convert legacy SM-2 progress into FSRS stability/difficulty.
 * @param {Record<string, unknown>} data
 */
export function migrateToFsrsState(data) {
  if (Number.isFinite(Number(data.stability)) && Number.isFinite(Number(data.difficulty))) {
    return {
      stability: +Number(data.stability).toFixed(2),
      difficulty: constrainDifficulty(Number(data.difficulty))
    };
  }

  const scheduledDays = Math.max(Number(data.interval) || 0, 0.1);
  const easeFactor = Number(data.easiness) || 2.5;
  const stability = +scheduledDays.toFixed(2);
  const difficulty = constrainDifficulty(
    11 - (easeFactor - 1) / (
      Math.exp(FSRS_W[8]) *
      stability ** -FSRS_W[9] *
      (Math.exp(0.1 * FSRS_W[10]) - 1)
    )
  );

  return { stability, difficulty };
}

function elapsedDaysSince(lastReviewed, now) {
  if (!lastReviewed) return 0;
  const last = new Date(lastReviewed);
  if (Number.isNaN(last.getTime())) return 0;
  return Math.max(0, (now - last) / (1000 * 60 * 60 * 24));
}

function addMinutes(now, minutes) {
  return new Date(now.getTime() + minutes * 60 * 1000);
}

function learningStepsForPhase(phase) {
  return phase === CARD_PHASE.RELEARNING ? RELEARNING_STEPS_MINUTES : LEARNING_STEPS_MINUTES;
}

/**
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {string}
 */
export function resolveCardPhase(data) {
  if (!data || typeof data !== 'object') return CARD_PHASE.NEW;
  if (data.cardPhase && Object.values(CARD_PHASE).includes(data.cardPhase)) {
    return data.cardPhase;
  }
  const totalReviews = Number(data.totalReviews) || 0;
  const interval = Number(data.interval) || 0;
  const intervalMinutes = Number(data.intervalMinutes) || 0;
  if (totalReviews === 0) return CARD_PHASE.NEW;
  if (intervalMinutes > 0 || interval < 1) {
    return CARD_PHASE.LEARNING;
  }
  if (interval >= GRADUATION_INTERVAL_DAYS && (Number(data.repetitions) || 0) >= 1) {
    return CARD_PHASE.REVIEW;
  }
  return CARD_PHASE.LEARNING;
}

/**
 * @param {Record<string, unknown> | null | undefined} data
 */
export function isCardInLearningPhase(data) {
  const phase = resolveCardPhase(data);
  return (
    phase === CARD_PHASE.NEW ||
    phase === CARD_PHASE.LEARNING ||
    phase === CARD_PHASE.RELEARNING
  );
}

/**
 * Compact Anki-style interval label from minutes (e.g. 10m, 1h, 1d).
 * @param {number} totalMinutes
 */
function formatAnkiIntervalFromMinutes(totalMinutes) {
  const minutes = Math.max(1, Math.round(totalMinutes));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

/**
 * Resolve the actual datetime when a card becomes due again.
 * Handles same-session learning (nextReview stored as now + intervalMinutes).
 * @param {Record<string, unknown> | null | undefined} progress
 * @param {Date} [now]
 * @returns {Date | null}
 */
export function resolveSrsNextReviewDate(progress, now = new Date()) {
  if (!progress?.nextReview) return null;

  const minutes = Number(progress.intervalMinutes);
  if (Number.isFinite(minutes) && minutes > 0 && isCardInLearningPhase(progress)) {
    const stored = new Date(progress.nextReview);
    if (!Number.isNaN(stored.getTime()) && stored.getTime() > now.getTime() + 30 * 1000) {
      return stored;
    }
    return addMinutes(now, minutes);
  }

  const intervalDays = Number(progress.interval);
  const phase = resolveCardPhase(progress);
  let due = new Date(progress.nextReview);
  if (Number.isNaN(due.getTime())) return null;

  if (
    phase === CARD_PHASE.REVIEW &&
    Number.isFinite(intervalDays) &&
    intervalDays >= 1
  ) {
    const daysDiff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    const lastReviewed = progress.lastReviewed ? new Date(progress.lastReviewed) : null;
    const reviewedRecently = lastReviewed
      && !Number.isNaN(lastReviewed.getTime())
      && (now.getTime() - lastReviewed.getTime()) < 5 * 60 * 1000;

    // Same-session stub: interval advanced but nextReview still "now" from a prior write.
    if (daysDiff <= 0 && reviewedRecently) {
      due = new Date(now);
      due.setDate(due.getDate() + intervalDays);
    }
  }

  return due;
}

/**
 * Anki-style next-step label: minutes or days only (10m, 15m, 6d).
 * @param {Record<string, unknown> | null | undefined} progress
 * @param {Date} [now]
 */
export function formatSrsScheduleLabel(progress, now = new Date()) {
  if (!progress?.nextReview) return '';

  const minutes = Number(progress.intervalMinutes);
  if (Number.isFinite(minutes) && minutes > 0 && isCardInLearningPhase(progress)) {
    return formatAnkiIntervalFromMinutes(minutes);
  }

  const intervalDays = Number(progress.interval);
  const phase = resolveCardPhase(progress);
  if (
    phase === CARD_PHASE.REVIEW &&
    Number.isFinite(intervalDays) &&
    intervalDays >= 1
  ) {
    return `${Math.round(intervalDays)}d`;
  }

  const due = resolveSrsNextReviewDate(progress, now);
  if (!due) return '';

  const msUntil = Math.max(0, due.getTime() - now.getTime());
  const totalMinutes = Math.ceil(msUntil / (60 * 1000));
  return formatAnkiIntervalFromMinutes(totalMinutes);
}

function buildScheduleResult({
  stability,
  difficulty,
  repetitions,
  easiness,
  interval,
  intervalMinutes,
  cardPhase,
  learningStep,
  lastRating,
  nextReview,
  now
}) {
  return {
    stability: +stability.toFixed(2),
    difficulty: +difficulty.toFixed(2),
    repetitions,
    easiness: +easiness.toFixed(2),
    interval,
    intervalMinutes: intervalMinutes ?? null,
    cardPhase,
    learningStep,
    lastRating,
    nextReview: nextReview.toISOString()
  };
}

function scheduleFsrsReview({
  currentData,
  rating,
  stability,
  difficulty,
  repetitions,
  previousInterval,
  seed,
  now
}) {
  const elapsed = elapsedDaysSince(currentData.lastReviewed, now);
  const retrievability = forgettingCurve(elapsed, stability);
  const lastS = stability;
  const lastD = difficulty;

  const hardStability = nextRecallStability(lastD, lastS, retrievability, 'hard');
  const goodStability = nextRecallStability(lastD, lastS, retrievability, 'good');
  const easyStability = nextRecallStability(lastD, lastS, retrievability, 'easy');

  let hardInterval = nextInterval(hardStability, seed, previousInterval);
  let goodInterval = nextInterval(goodStability, seed, previousInterval);
  let easyInterval = nextInterval(easyStability, seed, previousInterval);

  hardInterval = Math.min(hardInterval, goodInterval);
  goodInterval = Math.max(goodInterval, hardInterval + 1);
  easyInterval = Math.max(easyInterval, goodInterval + 1);

  const nextStability = rating === 'hard'
    ? hardStability
    : rating === 'good'
      ? goodStability
      : easyStability;
  const updatedDifficulty = nextDifficulty(lastD, rating);
  const nextRepetitions = repetitions + 1;
  const nextIntervalDays = rating === 'hard'
    ? hardInterval
    : rating === 'good'
      ? goodInterval
      : easyInterval;

  const easiness = Math.min(2.5, Math.max(1.3, 2.5 - (updatedDifficulty - 5) * 0.12));
  const clampedInterval = Math.min(Math.max(nextIntervalDays, 1), SRS_MAX_INTERVAL_DAYS);
  const nextReview = new Date(now);
  nextReview.setDate(nextReview.getDate() + clampedInterval);

  return buildScheduleResult({
    stability: nextStability,
    difficulty: updatedDifficulty,
    repetitions: nextRepetitions,
    easiness,
    interval: clampedInterval,
    intervalMinutes: null,
    cardPhase: CARD_PHASE.REVIEW,
    learningStep: 0,
    lastRating: rating,
    nextReview,
    now
  });
}

/**
 * @deprecated Legacy SM-2 helper kept for tests/migration reference.
 */
export function calculateSM2Interval(repetitions, easiness, previousInterval) {
  if (repetitions === 0) return 1;
  if (repetitions === 1) return 3;
  if (repetitions === 2) return 7;
  return Math.round(Math.min(previousInterval * easiness, SRS_MAX_INTERVAL_DAYS));
}

/**
 * Calculate mastery based on performance and time decay.
 * @param {number} currentMastery
 * @param {boolean | FsrsRatingName} performance
 * @param {number} streak
 * @param {string|Date} lastReviewed
 * @param {Date} [now]
 */
export function calculateMastery(currentMastery, performance, streak, lastReviewed, now = new Date()) {
  const rating = normalizeRating(performance);
  const wasSuccessful = isSuccessfulRating(rating);

  const lastReviewDate = new Date(lastReviewed || now);
  const daysSinceReview = (now - lastReviewDate) / (1000 * 60 * 60 * 24);
  const decayAmount = BASE_MASTERY.DECAY_RATE * daysSinceReview;

  let mastery = Math.max(currentMastery - decayAmount, BASE_MASTERY.MIN_MASTERY);

  const streakBonus = Math.min(streak * 0.1, 0.5);
  let performanceScore;
  if (!wasSuccessful) {
    performanceScore = BASE_MASTERY.FAILURE_PENALTY_FACTOR;
  } else if (rating === 'easy') {
    performanceScore = 1 + streakBonus + 0.25;
  } else if (rating === 'hard') {
    performanceScore = 0.75 + streakBonus * 0.5;
  } else {
    performanceScore = 1 + streakBonus;
  }

  mastery += BASE_MASTERY.CORRECT_INCREMENT * performanceScore;
  mastery = Math.min(Math.max(mastery, BASE_MASTERY.MIN_MASTERY), BASE_MASTERY.MAX_MASTERY);

  return Math.round(mastery);
}

/**
 * FSRS next-review calculation with Anki-style learning/relearning minute steps.
 * @param {Record<string, unknown>} currentData
 * @param {boolean | FsrsRatingName | { rating?: FsrsRatingName }} ratingInput
 * @param {Date} [now]
 * @param {{ cardKey?: string, sameSessionContinue?: boolean }} [options]
 */
export function calculateNextReview(currentData, ratingInput, now = new Date(), options = {}) {
  const rating = normalizeRating(ratingInput);
  const previousInterval = Number(currentData.interval) || 0;
  const seed = hashSeed(`${options.cardKey || ''}:${currentData.totalReviews || 0}:${rating}`);

  let { stability, difficulty } = migrateToFsrsState(currentData);
  let repetitions = Number(currentData.repetitions) || 0;
  let cardPhase = resolveCardPhase(currentData);
  let learningStep = Number(currentData.learningStep) || 0;

  // Lapsed review cards re-enter the relearning minute ladder.
  if (cardPhase === CARD_PHASE.REVIEW && rating === 'again') {
    const elapsed = elapsedDaysSince(currentData.lastReviewed, now);
    const retrievability = forgettingCurve(elapsed, stability);
    stability = nextForgetStability(difficulty, stability, retrievability);
    difficulty = nextDifficulty(difficulty, rating);
    repetitions = 0;
    cardPhase = CARD_PHASE.RELEARNING;
    learningStep = 0;
  }

  if (cardPhase === CARD_PHASE.NEW) {
    cardPhase = CARD_PHASE.LEARNING;
    learningStep = 0;
  }

  if (cardPhase === CARD_PHASE.LEARNING || cardPhase === CARD_PHASE.RELEARNING) {
    const steps = learningStepsForPhase(cardPhase);
    let intervalMinutes = steps[0];

    if (rating === 'again') {
      learningStep = 0;
      intervalMinutes = steps[0];
    } else if (rating === 'good') {
      learningStep += 1;
      if (learningStep < steps.length) {
        intervalMinutes = steps[learningStep];
      }
    } else if (rating === 'easy') {
      if (learningStep >= steps.length - 1) {
        // Already at the final minute step — graduate (avoids auto-grade deadlock when
        // inferRating maps perfect recall to easy on every Check).
        learningStep = steps.length;
      } else {
        // Option C: advance through remaining minute steps without skipping to review.
        learningStep = Math.min(learningStep + 2, steps.length - 1);
        intervalMinutes = steps[learningStep];
      }
    } else if (rating === 'hard') {
      intervalMinutes = steps[Math.min(learningStep, steps.length - 1)];
    }

    ({ stability, difficulty } = applyLearningMemoryUpdate(stability, difficulty, rating));

    if (
      learningStep >= steps.length &&
      (rating === 'good' || rating === 'easy')
    ) {
      repetitions = Math.max(repetitions, 1);
      const graduationPreviousInterval = cardPhase === CARD_PHASE.RELEARNING
        ? GRADUATION_INTERVAL_DAYS
        : Math.max(previousInterval, GRADUATION_INTERVAL_DAYS);
      const graduated = scheduleFsrsReview({
        currentData,
        rating,
        stability,
        difficulty,
        repetitions,
        previousInterval: graduationPreviousInterval,
        seed,
        now
      });
      return graduated;
    }

    const nextReview = addMinutes(now, intervalMinutes);
    const easiness = Math.min(2.5, Math.max(1.3, 2.5 - (difficulty - 5) * 0.12));

    return buildScheduleResult({
      stability,
      difficulty,
      repetitions: rating === 'again' ? 0 : repetitions,
      easiness,
      interval: 0,
      intervalMinutes,
      cardPhase,
      learningStep,
      lastRating: rating,
      nextReview,
      now
    });
  }

  if (rating === 'again') {
    const elapsed = elapsedDaysSince(currentData.lastReviewed, now);
    const retrievability = forgettingCurve(elapsed, stability);
    stability = nextForgetStability(difficulty, stability, retrievability);
    difficulty = nextDifficulty(difficulty, rating);
    repetitions = 0;
    const steps = RELEARNING_STEPS_MINUTES;
    const nextReview = addMinutes(now, steps[0]);
    const easiness = Math.min(2.5, Math.max(1.3, 2.5 - (difficulty - 5) * 0.12));
    return buildScheduleResult({
      stability,
      difficulty,
      repetitions: 0,
      easiness,
      interval: 0,
      intervalMinutes: steps[0],
      cardPhase: CARD_PHASE.RELEARNING,
      learningStep: 0,
      lastRating: rating,
      nextReview,
      now
    });
  }

  return scheduleFsrsReview({
    currentData,
    rating,
    stability,
    difficulty,
    repetitions,
    previousInterval,
    seed,
    now
  });
}

/**
 * @param {number} mastery
 * @returns {string}
 */
export function getMasteryLevel(mastery) {
  for (const [, range] of Object.entries(MASTERY_LEVELS)) {
    if (mastery >= range.min && mastery <= range.max) {
      return range.name;
    }
  }
  return MASTERY_LEVELS.BEGINNER.name;
}

export const MASTERY_THRESHOLD = MASTERY_LEVELS.MASTER.min;
export const MIN_REVIEWS_FOR_MASTERY = 5;
export const MIN_REPETITIONS_FOR_MASTERY = 4;
export const MIN_INTERVAL_DAYS_FOR_MASTERY = 14;
export const MIN_STUDY_SPAN_DAYS_FOR_MASTERY = 7;

/**
 * @param {string|Date|undefined} from
 * @param {string|Date|undefined} to
 */
export function studySpanDays(from, to) {
  if (!from || !to) return 0;
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
}

/**
 * @param {Record<string, unknown> | null | undefined} progress
 */
export function isCardMastered(progress) {
  if (!progress || typeof progress !== 'object') return false;
  if (isCardInLearningPhase(progress)) return false;
  const mastery = Number(progress.mastery);
  const totalReviews = Number(progress.totalReviews);
  const repetitions = Number(progress.repetitions);
  const interval = Number(progress.interval);
  if (!Number.isFinite(mastery) || !Number.isFinite(totalReviews)) return false;
  if (totalReviews < MIN_REVIEWS_FOR_MASTERY) return false;
  if (mastery < MASTERY_THRESHOLD) return false;
  if (!Number.isFinite(repetitions) || repetitions < MIN_REPETITIONS_FOR_MASTERY) return false;
  if (!Number.isFinite(interval) || interval < MIN_INTERVAL_DAYS_FOR_MASTERY) return false;

  const firstReviewed = progress.firstReviewed;
  const lastReviewed = progress.lastReviewed;
  if (!firstReviewed || !lastReviewed) return false;
  if (studySpanDays(firstReviewed, lastReviewed) < MIN_STUDY_SPAN_DAYS_FOR_MASTERY) {
    return false;
  }

  return true;
}
